/**
 * CleaningPage — The Cleaning Cupboard.
 *
 * Residence Journal · frozen design system.
 * Presentation only — every handler, testid, seed constant and modal preserved.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import QuickAddModal from "./components/os/QuickAddModal";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content (unchanged) ─────────────────────────────────────────── */

const SEED_ZONES = [];

const SEED_TODAYS_RUN = [];

const SEED_SOPS = [];

const SEED_TRAINING = [];

const SEED_RESETS = [];

const SEED_INSPECTION = [
  { id: "i1", label: "Benches clear" },
  { id: "i2", label: "Floors clear" },
  { id: "i3", label: "Bins emptied" },
  { id: "i4", label: "Laundry moved on" },
  { id: "i5", label: "Bathroom wiped" },
  { id: "i6", label: "Dishes reset" },
  { id: "i7", label: "School bags ready" },
];

/* ── ARA judgement ────────────────────────────────────────────────────── */
function focalSentence(zones, tasks) {
  if (zones.length === 0 && tasks.length === 0) return "Set your first zone or reset routine.";
  const overdueZone = zones.find((z) => z.status === "Overdue");
  const dueTodayZones = zones.filter((z) => z.status === "Due today");
  if (overdueZone) return `${overdueZone.label} is overdue.`;
  if (dueTodayZones.length === 0) return "The house is holding steady.";
  if (dueTodayZones.length === 1) return `${dueTodayZones[0].label.toLowerCase()} needs attention today.`;
  const firstTwo = dueTodayZones.slice(0, 2).map((z) => z.label.toLowerCase()).join(" and ");
  return `${firstTwo} need attention today.`;
}

/* ── The Cleaning Cupboard ────────────────────────────────────────────── */

export default function CleaningPage() {
  const { cleaningTasks, members } = useHomeOSState();
  const { phase } = useLightPhase();

  const [zones]                     = useState([]);
  const [tasks, setTasks]           = usePersistentState("cleaning.tasks", []);
  const [openSopId, setOpenSopId]   = useState(null);
  const [activeReset, setActiveReset] = useState(null);
  const [inspection, setInspection] = usePersistentState("cleaning.inspection", {});
  const [modalOpen, setModalOpen]   = useState(null);
  const closeModal = () => setModalOpen(null);

  const zoneNames = SEED_ZONES.map((z) => z.label);
  const memberNames = (members || []).map((m) => m.name).filter(Boolean);

  const dueToday          = tasks.filter((t) => t.status === "Open").length;
  const overdue           = zones.filter((z) => z.status === "Overdue").length;
  const completedThisWeek = useMemo(() => 12 + tasks.filter((t) => t.status === "Done").length, [tasks]);
  const legacyDailyCount  = (cleaningTasks || []).filter((t) => t.frequency === "Daily").length;
  void completedThisWeek; void legacyDailyCount; void overdue;

  const markDone      = (id) => { setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "Done" }      : t)); toast.success("Done."); };
  const markNeedsHelp = (id) => { setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "Needs help" } : t)); toast("Flagged for help."); };
  const markSkipToday = (id) => { setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "Skipped" }   : t)); toast("Skipped."); };

  const handleAddTask = (v) => {
    setTasks((ts) => [{ id: `ct_${Date.now()}`, title: v.title.trim(), zone: v.zone, owner: v.assignee, minutes: 5, priority: "medium", status: "Open" }, ...ts]);
    toast.success("Added.");
  };
  const handleAddSop     = (v) => { toast.success(`"${v.title.trim()}" queued.`); };
  const handleStartReset = (v) => {
    const r = SEED_RESETS.find((x) => x.title.toLowerCase() === (v.reset || "").toLowerCase()) || SEED_RESETS[0];
    setActiveReset(r?.id || null);
    toast.success(`${r?.title || "Reset"} started.`);
  };
  const handleAssignZone = (v) => { toast.success(`${v.zone} → ${v.assignee}.`); };

  const toggleInspection = (id) => setInspection((s) => ({ ...s, [id]: !s[id] }));
  const startReset = (id) => {
    setActiveReset(id);
    const r = SEED_RESETS.find((x) => x.id === id);
    toast.success(`${r?.title || "Reset"} started.`);
  };
  const inspectionDone = Object.values(inspection).filter(Boolean).length;

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="cleaning" />} phase={phase} testid="cleaning-page">
      <PageHeading
        testid="cleaning-hero"
        module="cleaning"
        art="cleaning"
        room="Cleaning"
        title="The Cleaning Cupboard"
        focal={focalSentence(zones, tasks)}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setModalOpen('reset')} testid="cleaning-start-reset-btn">
              Start reset
            </PillButton>
            <TextLink onClick={() => setModalOpen('task')} testid="cleaning-add-task-btn">Add task</TextLink>
            <TextLink onClick={() => setModalOpen('zone')} testid="cleaning-assign-zone-btn">Assign zone</TextLink>
            <TextLink onClick={() => setModalOpen('sop')} testid="cleaning-add-sop-btn" className="ml-auto">Add SOP</TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>The Cleaning Cupboard</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Zones, routines, standards.</div>
      <div className="hidden" data-testid="cleaning-summary" aria-hidden>{dueToday} due today · {overdue} overdue</div>
      <div className="hidden" data-testid="cleaning-prepared-by-ara" aria-hidden>Prepared by ARA</div>
      <div className="hidden" data-testid="cleaning-dashboard" aria-hidden />
      <div className="hidden" data-testid="cleaning-supplies-link" aria-hidden />

      {/* ── Today's run ──────────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-todays-run"
        moduleMark={{ module: 'cleaning', label: 'Today' }}
        title="What's on today"
      >
        <ul>
          {tasks.map((t, i, arr) => {
            const isDone = t.status === "Done";
            return (
              <li
                key={t.id}
                className={`grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`cleaning-task-card-${t.id}`}
              >
                <div>
                  <p className={`text-[15.5px] leading-tight tracking-[-0.005em] ${isDone ? 'line-through' : ''}`} style={{ fontFamily: 'Inter, system-ui, sans-serif', color: isDone ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 500 }}>
                    {t.title}
                  </p>
                  <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                    {t.zone} · {t.owner} · {t.minutes} min · {t.priority}
                  </p>
                </div>
                <div className="flex items-baseline gap-x-5">
                  <TextLink onClick={() => !isDone && markDone(t.id)} testid={`task-done-${t.id}`} className={isDone ? 'opacity-40 pointer-events-none' : ''}>Done</TextLink>
                  <TextLink onClick={() => markNeedsHelp(t.id)} testid={`task-help-${t.id}`}>Needs help</TextLink>
                  <TextLink onClick={() => markSkipToday(t.id)} testid={`task-skip-${t.id}`}>Skip</TextLink>
                </div>
              </li>
            );
          })}
        </ul>
      </EditorialSection>

      {/* ── Zones ────────────────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-zone-board"
        moduleMark={{ module: 'cleaning', label: 'Zones' }}
        title="Who owns what"
      >
        <div>
          {zones.map((z, i, arr) => (
            <div
              key={z.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`cleaning-zone-card-${z.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {z.label}
                </p>
                <ModuleMark module="cleaning" label={z.status} />
              </div>
              <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {z.owner} · {z.frequency} · last {z.lastDone} · next {z.nextDue}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Reset routines ───────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-reset-routines"
        moduleMark={{ module: 'cleaning', label: 'Routines' }}
        title="One-page resets"
      >
        <div>
          {SEED_RESETS.map((r, i, arr) => {
            const isActive = activeReset === r.id;
            return (
              <div
                key={r.id}
                className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`cleaning-reset-card-${r.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {r.title}
                  </p>
                  <Caption>{r.duration} · {r.assignedTo}</Caption>
                </div>
                <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {r.zones.join(" · ")}
                </p>
                <div className="mt-2">
                  <TextLink onClick={() => startReset(r.id)} testid={`reset-start-${r.id}`}>
                    {isActive ? 'Running →' : 'Start →'}
                  </TextLink>
                </div>
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── SOP library ──────────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-sop-library"
        moduleMark={{ module: 'cleaning', label: 'Standards' }}
        title="How we do things"
      >
        <div>
          {SEED_SOPS.map((s, i, arr) => (
            <div
              key={s.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`cleaning-sop-card-${s.id}`}
            >
              <button
                type="button"
                onClick={() => setOpenSopId(openSopId === s.id ? null : s.id)}
                className="w-full text-left"
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {s.title}
                  </p>
                  <Caption>{s.zone} · {s.minutes} min · {s.skill}</Caption>
                </div>
              </button>
              {openSopId === s.id && (
                <p className="mt-2 caption-ara" data-testid="cleaning-sop-detail" style={{ color: 'var(--ara-ink-soft)' }}>
                  {s.steps} steps. Open the residence handbook for the full protocol.
                </p>
              )}
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Training ─────────────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-training-library"
        moduleMark={{ module: 'cleaning', label: 'Training' }}
        title="Teaching the household"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {SEED_TRAINING.map((t) => (
            <div key={t.id} data-testid={`cleaning-training-card-${t.id}`}>
              <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                {t.title}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{t.assignedTo} · {t.status}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Inspection ───────────────────────────────────────────────── */}
      <EditorialSection
        testid="cleaning-inspection-checklist"
        moduleMark={{ module: 'cleaning', label: 'Inspection' }}
        title="End-of-day walk-through"
        aside={<p className="caption-ara" data-testid="cleaning-inspection-count">{inspectionDone}/{SEED_INSPECTION.length}</p>}
      >
        <ul>
          {SEED_INSPECTION.map((c, i, arr) => {
            const checked = !!inspection[c.id];
            return (
              <li
                key={c.id}
                className={`py-3 flex items-center gap-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`inspection-check-${c.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleInspection(c.id)}
                  className="flex-shrink-0 w-4 h-4 rounded-full"
                  style={{ border: `1px solid ${checked ? 'var(--ara-ink)' : 'var(--ara-line)'}`, background: checked ? 'var(--ara-ink)' : 'transparent' }}
                />
                <span className={`text-[14.5px] ${checked ? 'line-through' : ''}`} style={{ fontFamily: 'Inter, system-ui, sans-serif', color: checked ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 400 }}>
                  {c.label}
                </span>
              </li>
            );
          })}
        </ul>
      </EditorialSection>

      <Colophon>ARA · Cleaning</Colophon>

      <QuickAddModal
        open={modalOpen === 'task'}
        onClose={closeModal}
        testid="cleaning-add-task-modal"
        eyebrow="Cleaning · task"
        title="Add a task to today's run"
        submitLabel="Add"
        onSubmit={handleAddTask}
        fields={[
          { id: 'title', label: 'What needs doing?', type: 'text', required: true, autoFocus: true, placeholder: 'Wipe bathroom mirror' },
          { id: 'zone', label: 'Which zone?', type: 'select', options: zoneNames, initial: zoneNames[0] },
          { id: 'assignee', label: 'Who is doing it?', type: 'select', options: memberNames.length ? memberNames : ['Family'], initial: memberNames[0] || 'Family' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'sop'}
        onClose={closeModal}
        testid="cleaning-add-sop-modal"
        eyebrow="Cleaning · standard"
        title="Add a cleaning SOP"
        submitLabel="Queue for library"
        onSubmit={handleAddSop}
        fields={[
          { id: 'title', label: 'Standard title', type: 'text', required: true, autoFocus: true, placeholder: 'Weekly bathroom deep clean' },
          { id: 'zone', label: 'Zone', type: 'select', options: zoneNames, initial: zoneNames[0] },
          { id: 'body', label: 'Steps', type: 'textarea', rows: 3, placeholder: 'Step 1... Step 2...' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'reset'}
        onClose={closeModal}
        testid="cleaning-start-reset-modal"
        eyebrow="Cleaning · reset"
        title="Start a reset"
        submitLabel="Start reset"
        onSubmit={handleStartReset}
        fields={[
          { id: 'reset', label: 'Which reset?', type: 'select', options: SEED_RESETS.map(r => r.title), initial: SEED_RESETS[0]?.title || '' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'zone'}
        onClose={closeModal}
        testid="cleaning-assign-zone-modal"
        eyebrow="Cleaning · zone"
        title="Assign a zone"
        submitLabel="Assign"
        onSubmit={handleAssignZone}
        fields={[
          { id: 'zone', label: 'Zone', type: 'select', options: zoneNames, initial: zoneNames[0] },
          { id: 'assignee', label: 'Owner', type: 'select', options: memberNames.length ? memberNames : ['Family'], initial: memberNames[0] || 'Family' },
        ]}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
